import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import {
  Card,
  DataTable,
  TableHeader,
  TableBody,
  TableRow,
  TableColumn,
} from 'react-md';

import ContestedTableActions from './ContestedTableActions';
import { invokeBackendApi } from '../../libs/awsLib';
import ContestedRow from './ContestedRow';

export default injectIntl(class Contested extends PureComponent {
  static propTypes = {
    intl: intlShape.isRequired,
    segmentsByDriver: PropTypes.array,
    userAttributes: PropTypes.shape({
      isPsy: PropTypes.bool.isRequired,
    }).isRequired,
    handleOnclick: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      segments: [],
      sortedSegments: [],
      sortColumn: -3,
      isLoading: false,
      acceptedItems: [],
      rejectedItems: [],
      selectedRows: [],
    };
  }
  componentWillReceiveProps({ segmentsByDriver }) {
    const segments = (segmentsByDriver[0] instanceof Array)
      ? segmentsByDriver.reduce((acc, val) => acc.concat(val), [])
      : segmentsByDriver;
    this.setState({ segments }, () => this.sort(this.state.sortColumn));
  }

  sort = (newSort) => {
    const { segments } = this.state;
    const displayedAttributes = ['avg', 'limit', 'start', 'driverName', 'formatted_address', 'GPSFixes', 'errorType'];
    const comparedAttribute = displayedAttributes[Math.abs(newSort) - 1];
    const dessending = newSort < 0;
    const sortedSegments = segments.slice().sort((a, b) => {
      if (dessending) [a, b] = [b, a];
      // eslint-disable-next-line prefer-template
      const ao = '' + a[comparedAttribute] || '';
      // eslint-disable-next-line prefer-template
      const bo = '' + b[comparedAttribute] || '';
      return (ao - bo) || ao.localeCompare(bo);
    });
    this.setState({ sortedSegments, sortColumn: newSort });
  };

  isSorted = (columnNumber) => {
    if (this.state.sortColumn === columnNumber) return true;
    else if (this.state.sortColumn === -columnNumber) return false;
    return undefined;
  };

  handleClickColumnHeader = (columnNumber) => {
    const ascending = !this.isSorted(columnNumber);
    const newSort = ascending ? columnNumber : -columnNumber;
    this.sort(newSort);
  }

  handleRowToggle = (row, selected, count, event) => {
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();

    const {
      selectedRows, acceptedItems, reportedItems, rejectedItems, sortedSegments,
    } = this.state;
    const { userAttributes: { isPsy } } = this.props;

    let newSelectedRows;
    if (row === 0) {
      newSelectedRows = selected ? sortedSegments.filter(s => s.segmentState !== 'ACCEPTED' &&
      s.segmentState !== 'REJECTED' &&
      !acceptedItems.includes(s.segmentId) &&
      !rejectedItems.includes(s.segmentId) &&
      (isPsy || (s.segmentState !== 'REPORTED' && !reportedItems.includes(s.segmentId))))
        .map(s => s.segmentId) : [];
    } else {
      const selectedSegmentId = sortedSegments[row - 1].segmentId;
      if (selectedRows.includes(selectedSegmentId)) {
        newSelectedRows = selectedRows.filter(sr => sr !== selectedSegmentId);
      } else {
        newSelectedRows = [...selectedRows, selectedSegmentId];
      }
    }
    this.setState({ selectedRows: newSelectedRows });
  };

  reportSelected = (segments) => {
    const { selectedRows, acceptedItems } = this.state;
    this.setState({ isLoading: true });
    const {
      userAttributes: { isPsy },
    } = this.props;

    if (isPsy) { // Accept batch method
      const path = '/segments/accept';
      const body = { segmentIds: segments };
      invokeBackendApi({ path, method: 'POST', body }).then(() => {
        this.setState({
          isLoading: false, acceptedItems: [...acceptedItems, ...selectedRows], selectedRows: [],
        });
      });
    }
  };

  rejectSelected = (segments) => {
    const { selectedRows, rejectedItems } = this.state;
    this.setState({ isLoading: true });

    const invokeApiPromises = segments.map((segmentId) => {
      const path = `/segments/${segmentId}/reject`;
      return invokeBackendApi({ path, method: 'POST' });
    });

    Promise.all(invokeApiPromises).then(() => {
      this.setState({
        isLoading: false, rejectedItems: [...rejectedItems, ...selectedRows], selectedRows: [],
      });
    });
  }

  render() {
    const {
      intl, userAttributes, userAttributes: { isPsy }, handleOnclick,
    } = this.props;
    const {
      sortedSegments, selectedRows, acceptedItems, rejectedItems, isLoading,
    } = this.state;

    const dataTableProps = isPsy ? { baseId: 'contested', onRowToggle: this.handleRowToggle } : { plain: true };
    return (
      <Card>
        {isPsy && <ContestedTableActions
          onReportClick={this.reportSelected}
          onRejectClick={this.rejectSelected}
          userAttributes={userAttributes}
          selectedRows={selectedRows}
        />}
        <DataTable className={`bottom-padded ${isPsy ? 'speedingsTable-psy' : 'speedingsTable'}`} {...dataTableProps}>
          <TableHeader>
            <TableRow>
              <TableColumn
                sorted={this.isSorted(1)}
                onClick={() => { this.handleClickColumnHeader(1); }}
              >
                {intl.formatMessage({ id: 'journeyDetails.speed' })}
              </TableColumn>
              <TableColumn
                sorted={this.isSorted(2)}
                onClick={() => { this.handleClickColumnHeader(2); }}
              >
                {intl.formatMessage({ id: 'journeyDetails.limit' })}
              </TableColumn>
              <TableColumn
                sorted={this.isSorted(3)}
                onClick={() => { this.handleClickColumnHeader(3); }}
              >
                {intl.formatMessage({ id: 'contested.date' })}
              </TableColumn>
              {isPsy &&
                <TableColumn
                  sorted={this.isSorted(4)}
                  onClick={() => { this.handleClickColumnHeader(4); }}
                >
                  {intl.formatMessage({ id: 'contested.driver' })}
                </TableColumn>
              }
              <TableColumn
                sorted={this.isSorted(5)}
                onClick={() => { this.handleClickColumnHeader(5); }}
              >
                {intl.formatMessage({ id: 'contested.location' })}
              </TableColumn>
              <TableColumn
                sorted={this.isSorted(6)}
                onClick={() => { this.handleClickColumnHeader(6); }}
              >
                {intl.formatMessage({ id: 'journeyDetails.gps' })}
              </TableColumn>
              <TableColumn
                sorted={this.isSorted(7)}
                onClick={() => { this.handleClickColumnHeader(7); }}
              >
                {intl.formatMessage({ id: 'contested.errorType' })}
              </TableColumn>
              <TableColumn>
                {intl.formatMessage({ id: 'contested.errorReason' })}
              </TableColumn>
              {isPsy &&
                <TableColumn className="md-text-center">
                  {intl.formatMessage({ id: 'contested.action' })}
                </TableColumn>
              }
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedSegments.map(segment => (
              <ContestedRow
                key={segment.segmentId}
                segment={segment}
                userAttributes={userAttributes}
                onSegmentClick={handleOnclick}
                selected={selectedRows.includes(segment.segmentId)}
                isLoading={isLoading && selectedRows.includes(segment.segmentId)}
                isAccepted={acceptedItems.includes(segment.segmentId)}
                isRejected={rejectedItems.includes(segment.segmentId)}
              />
            ))}
          </TableBody>
        </DataTable>
      </Card>
    );
  }
});



// WEBPACK FOOTER //
// ./src/components/Contested/index.js